// PR-15: i18n bootstrap. Card *content* is never translated — only the
// UI chrome (memory N1).
//
// Locale resolution chain (memory N1): browser detect → user preference
// → per-thread override. This module wires tier 1 (browser/localStorage
// via the detector); the app calls `i18n.changeLanguage()` to apply the
// user preference (`users.locale` from /api/me) and the active thread's
// `locale` once those resolve — see `useLocaleSync`.

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

/** Custom detector: read locale from URL path prefix `/zh/`, `/ja/`,
 *  `/de/`. Runs FIRST in the detection order so SEO landing pages
 *  (/zh/comparisons/notion-ai etc) render in their target locale on
 *  the very first paint — no flash-of-english before useEffect kicks
 *  in. Detector is read-only: it doesn't auto-cache to localStorage,
 *  so visiting /zh/... once doesn't permanently flip the language for
 *  /en pages the user visits later. */
const pathPrefixDetector = {
  name: 'pathPrefix' as const,
  lookup(): string | undefined {
    if (typeof window === 'undefined') return undefined
    // /zh-tw/ must precede /zh/ in the alternation. It maps to the i18n key
    // `zhtw` (hyphen-free) so `load: 'languageOnly'` doesn't fold Traditional
    // onto the Simplified `zh` bundle.
    const m = window.location.pathname.match(/^\/(zh-tw|zh|ja|de|fr|es|ko|it)(?:\/|$)/)
    if (!m) return undefined
    return m[1] === 'zh-tw' ? 'zhtw' : m[1]
  },
}
import en from './locales/en.json'
import zh from './locales/zh.json'
import ja from './locales/ja.json'
import de from './locales/de.json'
import fr from './locales/fr.json'
import zhtw from './locales/zhtw.json'
import es from './locales/es.json'
import ko from './locales/ko.json'
import it from './locales/it.json'

export const SUPPORTED_LOCALES = ['en', 'zh', 'ja', 'de', 'fr', 'zhtw', 'es', 'ko', 'it'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

/** localStorage key the detector caches the resolved locale under. */
export const LOCALE_STORAGE_KEY = 'draftlize:locale'

const languageDetector = new LanguageDetector()
languageDetector.addDetector(pathPrefixDetector)

void i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      zh: { translation: zh },
      ja: { translation: ja },
      de: { translation: de },
      // Partial bundles: only the marketing-page chrome keys are translated;
      // everything else falls back to en (these locales render only on their
      // SEO landing pages, which carry their own native body copy).
      fr: { translation: fr },
      zhtw: { translation: zhtw },
      es: { translation: es },
      ko: { translation: ko },
      it: { translation: it },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    // Fold regional tags (zh-CN, en-GB, ja-JP, de-AT, …) onto the base
    // language so a `navigator.language` of "zh-CN" resolves to our
    // "zh" bundle.
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    detection: {
      // pathPrefix first so SEO landings at /zh/X render in zh on the
      // first paint (no en flash). Then localStorage so an in-app
      // pick survives reload instantly. Navigator is the cold-start
      // default. pathPrefix is NOT in `caches` so it doesn't pollute
      // localStorage for subsequent /en visits.
      order: ['pathPrefix', 'localStorage', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false, // React already escapes
    },
  })

/** Narrow an arbitrary string to a supported `Locale`, or null. Used when
 *  reading `users.locale` / `threads.locale` (free-text TEXT columns). */
export function asLocale(value: string | null | undefined): Locale | null {
  return value && (SUPPORTED_LOCALES as readonly string[]).includes(value)
    ? (value as Locale)
    : null
}

/** Closest supported locale from `navigator.language`, falling back to
 *  'en'. Used by "Auto" in the settings picker — after clearing the
 *  user pref, we still need to apply *something* to i18next in-session. */
export function detectBrowserLocale(): Locale {
  const lang = (
    typeof navigator !== 'undefined' ? navigator.language : 'en'
  )
    ?.toLowerCase()
    .split('-')[0]
  return lang && (SUPPORTED_LOCALES as readonly string[]).includes(lang)
    ? (lang as Locale)
    : 'en'
}

export default i18n
